import { useQuery } from '@apollo/client';
import { EventData } from 'components/eventView/pages/eventData';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { useViewMapQueryParams } from 'hooks/map/useViewMapQueryParams';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { mergeGuideToEvent } from 'libs/utils/guide/mergeGuideToEvent';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { EVPageMobile } from 'pages/EventViewPage/EVPageMobile';
import { EVPagePC } from 'pages/EventViewPage/EVPagePC';
import { useContext, useEffect } from 'react';
import { useParams } from 'react-router';
import { CtxApp } from 'session/ctxApp';
import { CtxEventViewProvider } from 'session/ctxEventView';
import { languages } from 'static/config/language/languageConfig';
import { Localization } from 'static/localization/localization';

export const EventViewPage = (): JSX.Element => {
    const isWide = useIsWide();
    const ctxApp = useContext(CtxApp);
    const { eventId } = useParams() as { eventId: string };
    const { getTokenForGuideId } = useWorldMapLocalStorage();
    const params = useViewMapQueryParams();
    const event = EventData[eventId];

    function changeLanguage(language: string) {
        const lang = languages.find((e) => e.code === language);
        if (!lang) {
            return;
        }
        Localization.setLanguage(lang.code);
        ctxApp.setLanguage(lang.code);
    }

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: eventId,
            token: getTokenForGuideId(eventId),
            language: params.language
        },
        skip: Boolean(event) && !event.guideId
    });

    useEffect(() => {
        localStorage.removeItem('scrollPosition');
    }, []);

    useEffect(() => {
        if (!guideQuery.data?.guideView) {
            return;
        }
        const language = params.language ?? guideQuery.data?.guideView.language ?? 'en';
        changeLanguage(language);
    }, [guideQuery.data?.guideView]);

    //redirect Mobile => PC
    useEffect(() => {
        if (isWide) {
            const url = window.location.pathname;
            const regex = new RegExp(`^/event/${eventId}/[a-f0-9-]{36}/[a-f0-9-]{36}$`);

            if (regex?.test(url)) {
                console.log('URL matches the expected format.');
                //get the last part of the URL
                const storyId = url?.split('/')?.pop();
                if (storyId) {
                    window.location.href = `/event/${eventId}?detailId=${storyId}`;
                }
            }
        }
    }, []);

    if (guideQuery.data == null) {
        return null;
    }

    const mergedEvent = mergeGuideToEvent(guideQuery?.data?.guideView, event);

    const worldMap = guideQuery.data?.guideView?.worldMap;
    const stories = guideQuery.data?.guideView?.stories ?? [];

    const eventPath =
        guideQuery?.data?.guideView?.customPath ||
        guideQuery?.data?.guideView?.guideId ||
        event?.guideId;

    return (
        <CtxEventViewProvider
            stories={stories}
            worldMap={{
                ...worldMap,
                guide: {
                    guideId: guideQuery?.data?.guideView?.guideId,
                    title: guideQuery?.data?.guideView?.title,
                    availableLanguages: guideQuery?.data?.guideView?.availableLanguages,
                    language: guideQuery?.data?.guideView?.language,
                    __typename: 'Guide'
                }
            }}
            event={mergedEvent}
            isEmbed={false}
            forms={guideQuery.data?.guideView?.forms ?? []}
            isWide={isWide}
            isPreview={false}
            eventPath={eventPath}
        >
            {isWide ? <EVPagePC /> : <EVPageMobile />}
        </CtxEventViewProvider>
    );
};
